import { useState } from 'react';
import { Alert, FormControl, FormHelperText, InputLabel, NativeSelect } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@store/index';
import { useSaveLocalePreference } from '@hooks/useSaveLocalePreference';
import { supportedLanguages, type SupportedLanguage } from '../../i18n';
import { offeredLanguage } from '../../i18n/localePreference';
import { toDisplayError } from '../../utils/apiError';

/** The option value that means "no stored preference". */
const AUTOMATIC = '';

/**
 * REV-798: the language setting.
 *
 * A native <select>, so keyboard, screen-reader and mobile behaviour are the
 * platform's, with a visible label. "Automatic" stores no preference and uses
 * the organization's, then the browser's. Each language is named in its own
 * language and marked with `lang` (WCAG 3.1.2), so a screen reader pronounces
 * "Deutsch" as German whatever the page is in.
 *
 * The choice applies at once and is saved to the account; a failed save is
 * announced and the language the person chose stays applied.
 */
export default function LanguageSetting() {
  const { t } = useTranslation();
  const stored = useAuthStore((s) => s.user?.locale);
  const save = useSaveLocalePreference();
  const [error, setError] = useState<string | null>(null);

  const onChange = async (value: string) => {
    setError(null);
    try {
      await save(value === AUTOMATIC ? null : (value as SupportedLanguage));
    } catch (e) {
      setError(toDisplayError(e, t('settings.appearance.languageSaveFailed')));
    }
  };

  return (
    <FormControl sx={{ maxWidth: 360 }}>
      <InputLabel variant="standard" htmlFor="language-setting" shrink>
        {t('settings.appearance.language')}
      </InputLabel>
      <NativeSelect
        value={offeredLanguage(stored) ?? AUTOMATIC}
        onChange={(e) => void onChange(e.target.value)}
        inputProps={{ id: 'language-setting', 'aria-describedby': 'language-setting-help' }}
      >
        <option value={AUTOMATIC}>{t('settings.appearance.languageAutomatic')}</option>
        {Object.entries(supportedLanguages).map(([code, { nativeName }]) => (
          <option key={code} value={code} lang={code}>
            {nativeName}
          </option>
        ))}
      </NativeSelect>
      <FormHelperText id="language-setting-help">
        {t('settings.appearance.languageHelp')}
      </FormHelperText>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </FormControl>
  );
}
